import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IMaskModule } from 'angular-imask';
import { map, Observable, tap } from 'rxjs';
import { IMaskConstants } from '../../../../../../core/constants/imask';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { SelectTypeaheadDirective } from '../../../../../../shared/directives/select-typeahead/select-typeahead.directive';
import { OrderBillingInformationService } from '../../../shared/services/order-billing-information/order-billing-information.service';
import { OrderBillingInformationResponse } from '../../models/order-billing-information-response';
import { OrderBillingInformationFormConstants } from './constants/order-billing-information-form-constants';
import { OrderBillingInformationFormModalData } from './models/order-billing-information-form-modal-data';
import { OrderBillingInformationFormModel } from './models/order-billing-information-form-model';
import { SaveOrderBillingInformationRequest } from './models/save-order-billing-information-request';
import { OrderBillingInformationFormService } from './services/order-billing-information-form.service';

@Component({
    selector: 'app-order-billing-information-form-modal',
    templateUrl: './order-billing-information-form-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogTitle,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        DraggableModalWrapperComponent,
        SelectTypeaheadDirective,
        IMaskModule,
        RegexInputDirective,
    ],
})
export class OrderBillingInformationFormModalComponent implements OnInit {
    public readonly data =
        inject<OrderBillingInformationFormModalData>(MAT_DIALOG_DATA);
    private readonly alertService = inject(AlertService);
    private readonly dropdownService = inject(DropdownService);
    private readonly dialogRef = inject(
        MatDialogRef<OrderBillingInformationFormModalComponent>,
    );
    private readonly orderBillingInformationService = inject(
        OrderBillingInformationService,
    );
    private readonly orderBillingInformationFormService = inject(
        OrderBillingInformationFormService,
    );

    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;

    /**
     * The state code dropdown options.
     */
    stateCodeOptions: DropdownOption[] = [];

    /**
     * The form group for the order billing information form.
     */
    form: FormGroup<FormGroupType<OrderBillingInformationFormModel>> =
        this.orderBillingInformationFormService.getFormDefinition();

    /**
     * Whether the form is in a submittable state.
     */
    get isFormValid(): boolean {
        return this.orderBillingInformationFormService.isFormValid(this.form);
    }

    ngOnInit(): void {
        this.loadForm().subscribe(() => {
            this.loadDropdownData();
        });
    }

    /**
     * Saves the form data when the form is valid and displays a success alert upon completion.
     * Dialog is closed with a true value to indicate an update occurred.
     */
    onSave(): void {
        if (this.isFormValid) {
            this.saveFormDataToApi()
                .pipe(
                    tap(() => {
                        this.alertService.displaySuccessAlert(
                            OrderBillingInformationFormConstants.SUCCESS_ALERT_MESSAGE,
                        );
                    }),
                    tap(() => {
                        this.dialogRef.close(true);
                    }),
                )
                .subscribe();
        }
    }

    /**
     * Closes the modal without saving changes. Returns a false value to indicate no update occurred.
     */
    onCancel(): void {
        this.dialogRef.close(false);
    }

    /**
     * Loads the form data from the API.
     *
     * @returns An observable that completes when the form is loaded.
     */
    private loadForm(): Observable<void> {
        return this.orderBillingInformationService
            .get(this.data.orderBillingInformationId)
            .pipe(
                tap((response: OrderBillingInformationResponse) => {
                    this.bindForm(response);
                }),
                map(() => void 0),
            );
    }

    /**
     * Binds the form with the provided response data.
     *
     * @param response The order billing information response data.
     */
    private bindForm(response: OrderBillingInformationResponse): void {
        this.form.patchValue(response);
    }

    /**
     * Saves the form data to the API.
     *
     * @returns An observable that completes when the form is saved.
     */
    private saveFormDataToApi(): Observable<void> {
        return this.orderBillingInformationService.save(
            this.data.orderBillingInformationId,
            this.generateRequestObject(),
        );
    }

    /**
     * Generates the request object from the form data.
     *
     * @returns The save order billing information request object.
     */
    private generateRequestObject(): SaveOrderBillingInformationRequest {
        return {
            ...this.form.value,
        } as SaveOrderBillingInformationRequest;
    }

    /**
     * Loads the dropdown data for the form.
     */
    private loadDropdownData(): void {
        this.dropdownService
            .getStateCodes()
            .pipe(
                tap((stateCodeOptions: DropdownOption[]) => {
                    this.stateCodeOptions = stateCodeOptions;
                }),
            )
            .subscribe();
    }
}
